'use client'
import { PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import { usePathname } from 'next/navigation'
import useIntersection from 'Hooks/useIntersection'
import Header from 'Comps/nav/Header'
import RecentTalk from 'Comps/nav/RecentTalk'
import { DesktopHomeScrollContainerContext } from 'Comps/providers/Providers'
import { motion } from 'framer-motion'
import { ArrowUpOutlined } from '@ant-design/icons'
import PaymentTerms from 'Comps/nav/Footer'
import dynamic from 'next/dynamic'
import {
  PagePerformanceLifecycleContext,
  usePagePerformanceLifecycle,
} from '@/hooks/usePagePerformanceLifecycle'
import { ChatPagePerformanceLifecycle } from '@/hooks/chat/ChatPagePerformanceLifecycle'
import { ListPagePerformanceLifecycle } from '@/hooks/home/ListPagePerformanceLifecycle'
import type { PagePerformanceLifecycle } from '@/lib/page-performance/PagePerformanceLifecycle'
import { withoutLocale } from '@/locales'

const GlobalModalGroup = dynamic(
  () => import('@/components/modal/GlobalModalGroup'),
  { ssr: false }
)

const createPageLifecycle = (
  pathname: string,
  fallbackLifecycle: PagePerformanceLifecycle | null
): PagePerformanceLifecycle | null => {
  const segments = withoutLocale(pathname).split('/').filter(Boolean)

  if (segments.length === 0) {
    return new ListPagePerformanceLifecycle('home')
  }

  if (
    segments.length === 2 &&
    (segments[0] === 'chat' || segments[0] === 'custom-chat')
  ) {
    const routeKey = segments[1]
    return new ChatPagePerformanceLifecycle(routeKey)
  }

  return fallbackLifecycle
}

export default function DesktopAppLayout({ children }: PropsWithChildren) {
  const pathname = usePathname() ?? '/'
  const fallbackLifecycle = usePagePerformanceLifecycle()
  const pageLifecycle = useMemo(
    () => createPageLifecycle(pathname, fallbackLifecycle),
    [fallbackLifecycle, pathname]
  )
  useEffect(
    () => () => {
      if (
        process.env.NODE_ENV === 'production' &&
        pageLifecycle !== fallbackLifecycle
      ) {
        pageLifecycle?.dispose()
      }
    },
    [fallbackLifecycle, pageLifecycle]
  )
  const listRef = useRef<HTMLDivElement>(null)
  const { isIntersecting: atTop, childRef: topRef } = useIntersection({
    parentRef: listRef,
    rootMargin: '0px',
  })

  const scrollToTop = () => {
    const scroll = listRef?.current
    if (scroll) {
      scroll.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <PagePerformanceLifecycleContext.Provider value={pageLifecycle}>
      <div
        className="desktop-scrollbar-container mx-auto h-full max-w-[3840px] overflow-auto text-primary-text [scrollbar-gutter:stable_both-edges]"
        ref={listRef}
      >
        <div ref={topRef}></div>
        <Header />
        <div className="flex h-full w-full pl-1.5 pt-14">
          <RecentTalk />
          <div className="w-full min-w-0">
            <DesktopHomeScrollContainerContext.Provider value={listRef}>
              {children}
            </DesktopHomeScrollContainerContext.Provider>
          </div>
        </div>
        <PaymentTerms />

        {/*全局对话框 DOM 不在 layout 里，所以手机和电脑版都会显示*/}
        <GlobalModalGroup />

        {!atTop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute bottom-[28px] right-4 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-secondary-border bg-secondary-bg backdrop-blur-sm"
            onClick={scrollToTop}
          >
            <ArrowUpOutlined className="text-xl leading-none" />
          </motion.div>
        )}
      </div>
    </PagePerformanceLifecycleContext.Provider>
  )
}
